import { useEffect, useState, useRef, useCallback } from 'react'
import { useAuthStore } from '../stores'
import { LogOut, Settings2, MessageSquare, Lock } from 'lucide-react'
import { useProStatus } from '../utils/useProStatus'
import LogPlayer from '../components/LogPlayer'
import PlayerDatabase from '../components/PlayerDatabase'
import ScoutInsights from '../components/ScoutInsights'
import ClubDashboard from '../components/ClubDashboard'
import MatchRecorder from '../components/MatchRecorder'
import MatchReportGenerator from '../components/MatchReportGenerator'
import CSVImporter from '../components/CSVImporter'
import AdminAccounts from '../components/AdminAccounts'
import AdminInsights from '../components/AdminInsights'
import AdminClubs from '../components/AdminClubs'
import AdminClaims from '../components/AdminClaims'
import AdminAnnouncements from '../components/AdminAnnouncements'
import AdminNotifications from '../components/AdminNotifications'
import AdminBlogPosts from '../components/AdminBlogPosts'
import AdminFixtures from '../components/AdminFixtures'
import AdminAffiliates from '../components/AdminAffiliates'
import AdminReports from '../components/admin/AdminReports'
import AffiliateDashboard from '../components/AffiliateDashboard'
import ExplorePage from './ExplorePage'
import SettingsPanel from '../components/SettingsPanel'
import ScoutMessages from '../components/ScoutMessages'
import PlayerMessages from '../components/PlayerMessages'
import SavedPlayers from '../components/SavedPlayers'
import { useMessageStore } from '../stores/useMessageStore'
import { usePlayerStore } from '../stores'
import AdminLayout, { AdminPage, SUB_ADMIN_ALLOWED_PAGES } from '../components/admin/AdminLayout'
import AdminOverview from '../components/admin/AdminOverview'
import AdminUserDirectory from '../components/admin/AdminUserDirectory'

type TabType = 'explore' | 'log' | 'database' | 'insights' | 'global_insights' | 'club' | 'recorder' | 'report' | 'import' | 'accounts' | 'clubs' | 'claims' | 'announcements' | 'notifications' | 'blog' | 'fixtures' | 'settings' | 'messages' | 'saved' | 'affiliates'

const DASHBOARD_TAB_KEY = 'scoutang:dashboard:tab'
const DASHBOARD_CLUB_KEY = 'scoutang:dashboard:club'

// Sub-admins only get content-focused pages: overview (explore), blog,
// fixtures, announcements, notifications, settings. No user/club/claims management.
const SUB_ADMIN_TABS: TabType[] = ['explore', 'blog', 'fixtures', 'announcements', 'notifications', 'settings']

const isTabType = (value: string | null): value is TabType =>
  Boolean(value && ['explore', 'log', 'database', 'insights', 'global_insights', 'club', 'recorder', 'report', 'import', 'accounts', 'clubs', 'claims', 'announcements', 'notifications', 'blog', 'fixtures', 'settings', 'messages', 'saved', 'affiliates'].includes(value))

interface DashboardProps {
  initialPlayerId?: string | null
  initialTab?: TabType
}

function Dashboard({ initialPlayerId, initialTab }: DashboardProps) {
  const { user, logout } = useAuthStore()
  const { players } = usePlayerStore()
  const { unreadCount, fetchUnreadCount, refreshConversations } = useMessageStore()
  const { isPro } = useProStatus()
  const isAdminRole = user?.role === 'admin' || user?.role === 'sub_admin'
  const isSubAdmin = user?.role === 'sub_admin'
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const candidate: TabType = initialTab || (initialPlayerId ? 'database' : (isTabType(window.sessionStorage.getItem(DASHBOARD_TAB_KEY)) ? window.sessionStorage.getItem(DASHBOARD_TAB_KEY)! as TabType : 'explore'))
    return isSubAdmin && !SUB_ADMIN_TABS.includes(candidate) ? 'explore' : candidate
  })
  const [deepLinkedPlayer, setDeepLinkedPlayer] = useState<string | null>(initialPlayerId || null)
  const [messagePlayerId, setMessagePlayerId] = useState<string | null>(null)
  const [viewingClubId, setViewingClubId] = useState<string | null>(window.sessionStorage.getItem(DASHBOARD_CLUB_KEY))
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const currentPlayer = user?.role === 'player' ? players.find(p =>
    p.scout?.toLowerCase() === user.email?.toLowerCase() ||
    p.email?.toLowerCase() === user.email?.toLowerCase() ||
    p.name?.toLowerCase() === user.name?.toLowerCase()
  ) : null

  const pollMessages = useCallback(() => {
    if (!user) return
    if (user.role === 'player' && currentPlayer?.id) {
      refreshConversations(user.id, user.role, currentPlayer.id)
    } else {
      refreshConversations(user.id, user.role)
    }
  }, [user, currentPlayer?.id, refreshConversations])

  // Fetch unread count on mount and start polling
  useEffect(() => {
    if (!user) return
    if (user.role === 'player' && currentPlayer?.id) {
      fetchUnreadCount(user.id, user.role, currentPlayer.id)
    } else {
      fetchUnreadCount(user.id, user.role)
    }

    // Poll every 15 seconds
    pollingRef.current = setInterval(pollMessages, 15000)
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [user, currentPlayer?.id, fetchUnreadCount, pollMessages])

  // Re-fetch unread count when navigating to messages tab
  useEffect(() => {
    if (activeTab === 'messages' && user) {
      if (user.role === 'player' && currentPlayer?.id) {
        fetchUnreadCount(user.id, user.role, currentPlayer.id)
      } else {
        fetchUnreadCount(user.id, user.role)
      }
    }
  }, [activeTab, user, currentPlayer?.id, fetchUnreadCount])

  useEffect(() => {
    window.sessionStorage.setItem(DASHBOARD_TAB_KEY, activeTab)
  }, [activeTab])

  useEffect(() => {
    if (viewingClubId) {
      window.sessionStorage.setItem(DASHBOARD_CLUB_KEY, viewingClubId)
    } else {
      window.sessionStorage.removeItem(DASHBOARD_CLUB_KEY)
    }
  }, [viewingClubId])

  useEffect(() => {
    const handleMessagePlayer = (e: CustomEvent<{ playerId: string }>) => {
      setMessagePlayerId(e.detail.playerId)
      setActiveTab('messages')
    }
    window.addEventListener('scoutang:message-player', handleMessagePlayer as EventListener)
    // Check sessionStorage for pending message player (from other pages)
    const pending = window.sessionStorage.getItem('scoutang:message-player')
    if (pending) {
      window.sessionStorage.removeItem('scoutang:message-player')
      setMessagePlayerId(pending)
      setActiveTab('messages')
    }
    return () => window.removeEventListener('scoutang:message-player', handleMessagePlayer as EventListener)
  }, [])

  const showTab = (tab: TabType) => {
    // Scout users can only see their own tabs
    if (user?.role === 'scout' && ['club', 'scouts', 'clubs', 'claims', 'announcements', 'notifications'].includes(tab)) {
      setActiveTab('database')
      return
    }
    // Club users can access all tabs including scout features
    if (user?.role === 'club') {
      setActiveTab(tab)
      return
    }
    // Admin users can access all tabs (sub-admins only see their allowlist)
    if (isAdminRole) {
      if (isSubAdmin && !SUB_ADMIN_TABS.includes(tab)) {
        setActiveTab('explore')
        return
      }
      setActiveTab(tab)
      return
    }
    // Default for other roles
    if (['club', 'scouts', 'clubs', 'announcements', 'notifications'].includes(tab)) {
      setActiveTab('database')
      return
    }
    setActiveTab(tab)
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'explore':
        return <ExplorePage onLoginClick={() => {}} selectedPlayerId={deepLinkedPlayer} />
      case 'log':
        return <LogPlayer />
      case 'database':
        return <PlayerDatabase initialSelectedPlayer={deepLinkedPlayer} onPlayerSelected={() => setDeepLinkedPlayer(null)} />
      case 'insights':
        return isPro ? <ScoutInsights /> : (
          <div className="max-w-md mx-auto text-center py-16">
            <Lock size={40} className="text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Pro Feature</h3>
            <p className="text-sm text-gray-400 mb-4">Scout Insights is available for Pro subscribers only.</p>
            <a href="/pricing" className="px-5 py-2 bg-green-500 text-black font-bold rounded-xl hover:bg-green-400 transition text-sm">Upgrade to Pro</a>
          </div>
        )
      case 'club':
        return <ClubDashboard clubId={viewingClubId} />
      case 'recorder':
        return isPro ? <MatchRecorder /> : (
          <div className="max-w-md mx-auto text-center py-16">
            <Lock size={40} className="text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Pro Feature</h3>
            <p className="text-sm text-gray-400 mb-4">Match Recorder is available for Pro subscribers only.</p>
            <a href="/pricing" className="px-5 py-2 bg-green-500 text-black font-bold rounded-xl hover:bg-green-400 transition text-sm">Upgrade to Pro</a>
          </div>
        )
      case 'report':
        return isPro ? <MatchReportGenerator /> : (
          <div className="max-w-md mx-auto text-center py-16">
            <Lock size={40} className="text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Pro Feature</h3>
            <p className="text-sm text-gray-400 mb-4">Match Reports is available for Pro subscribers only.</p>
            <a href="/pricing" className="px-5 py-2 bg-green-500 text-black font-bold rounded-xl hover:bg-green-400 transition text-sm">Upgrade to Pro</a>
          </div>
        )
      case 'import':
        return <CSVImporter />
      case 'accounts':
        return <AdminAccounts />
      case 'global_insights':
        return <AdminInsights />
      case 'clubs':
        return (
          <AdminClubs
            onViewClubDashboard={(clubId) => {
              setViewingClubId(clubId)
              setActiveTab('club')
            }}
          />
        )
      case 'claims':
        return <AdminClaims />
      case 'announcements':
        return <AdminAnnouncements />
      case 'notifications':
        return <AdminNotifications />
      case 'blog':
        return <AdminBlogPosts />
      case 'fixtures':
        return <AdminFixtures />
      case 'fixtures':
        return <AdminFixtures />
      case 'settings':
        return <SettingsPanel />
      case 'messages':
        return user?.role === 'player' ? <PlayerMessages embedded /> : <ScoutMessages initialPlayerId={messagePlayerId} embedded />
      case 'saved':
        return <SavedPlayers onSelectPlayer={(id) => { setDeepLinkedPlayer(id); setActiveTab('database') }} />
      case 'affiliates':
        return user?.role === 'admin' ? <AdminAffiliates /> : <AffiliateDashboard />
      default:
        return null
    }
  }

  const tabs: TabType[] = user?.role === 'club' 
    ? ['explore', 'log', 'database', 'messages', 'saved', 'insights', 'club', 'recorder', 'report', 'settings', 'affiliates'] // Club users can access scout tabs
    : isSubAdmin
      ? SUB_ADMIN_TABS
      : user?.role === 'admin'
        ? ['explore', 'global_insights', 'accounts', 'clubs', 'claims', 'database', 'club', 'messages', 'saved', 'recorder', 'log', 'insights', 'import', 'announcements', 'notifications', 'blog', 'fixtures', 'settings', 'affiliates']
        : ['explore', 'log', 'database', 'messages', 'saved', 'recorder', 'insights', 'settings', 'affiliates']

  // Admin layout mapping
  const tabToAdminPage: Record<string, AdminPage> = {
    'explore': 'overview',
    'global_insights': 'insights',
    'accounts': 'users',
    'clubs': 'clubs',
    'claims': 'claims',
    'announcements': 'announcements',
    'notifications': 'notifications',
    'blog': 'blog',
    'fixtures': 'fixtures',
    'affiliates': 'affiliates',
    'settings': 'settings',
  }

  const adminPageToTab: Record<AdminPage, TabType> = {
    'overview': 'explore',
    'users': 'accounts',
    'clubs': 'clubs',
    'claims': 'claims',
    'insights': 'global_insights',
    'blog': 'blog',
    'fixtures': 'fixtures',
    'affiliates': 'affiliates',
    'announcements': 'announcements',
    'notifications': 'notifications',
    'reports': 'explore',
    'subscriptions': 'affiliates',
    'settings': 'settings',
  }

  const activeAdminPage = tabToAdminPage[activeTab] || 'overview'

  const handleAdminNavigate = (page: string) => {
    if (isSubAdmin && !SUB_ADMIN_ALLOWED_PAGES.includes(page as AdminPage)) {
      setActiveTab('explore')
      return
    }
    setActiveTab(adminPageToTab[page as AdminPage] || 'explore')
  }

  const renderAdminContent = () => {
    if (isSubAdmin && !SUB_ADMIN_ALLOWED_PAGES.includes(activeAdminPage)) {
      return <AdminOverview onNavigate={handleAdminNavigate} />
    }
    switch (activeAdminPage) {
      case 'overview': return <AdminOverview onNavigate={handleAdminNavigate} />
      case 'users': return <AdminUserDirectory />
      case 'clubs': return <AdminClubs onViewClubDashboard={(clubId) => { setViewingClubId(clubId); setActiveTab('club') }} />
      case 'claims': return <AdminClaims />
      case 'insights': return <AdminInsights />
      case 'blog': return <AdminBlogPosts />
      case 'fixtures': return <AdminFixtures />
      case 'affiliates': return <AdminAffiliates />
      case 'announcements': return <AdminAnnouncements />
      case 'notifications': return <AdminNotifications />
      case 'settings': return <SettingsPanel />
      case 'reports': return <AdminReports />
      default: return <AdminOverview onNavigate={handleAdminNavigate} />
    }
  }

  // Admin layout with sidebar
  if (isAdminRole) {
    return (
      <AdminLayout activePage={activeAdminPage} onNavigate={handleAdminNavigate} onLogout={logout}>
        {renderAdminContent()}
      </AdminLayout>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      {/* PREVIOUS DESIGN
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/scoutang-logo.svg" 
              alt="Scouta Logo" 
              className="w-10 h-10 cursor-pointer hover:opacity-80 transition"
              onClick={() => {
                window.sessionStorage.setItem('scoutang:view', 'dashboard')
                window.location.hash = '#/dashboard'
              }}
            />
            <div>
              <h1 className="text-2xl font-bold text-green-500">Scouta</h1>
              <p className="text-sm text-gray-400">
                {user?.name} ({user?.role === 'scout' ? 'Scout' : user?.role})
              </p>
            </div>
          </div>

          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 bg-red-900/20 hover:bg-red-900/40 border border-red-700 rounded text-red-400 transition"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      */}

      {/* NEW OPTIMIZED DESIGN */}
      <header className="bg-gray-900/80 backdrop-blur-md border-b border-gray-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/scoutang-logo.svg" 
              alt="Scouta Logo" 
              className="w-8 h-8 sm:w-10 sm:h-10 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => {
                window.sessionStorage.setItem('scoutang:view', 'dashboard')
                window.location.hash = '#/dashboard'
            }}
            />
            <div className="flex items-center gap-3">
              <div className="relative h-10 w-10 overflow-hidden rounded-2xl border border-white/10 bg-black/30 sm:h-12 sm:w-12">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt={`${user?.name || 'User'} avatar`} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-[#0f1d22] text-[11px] font-black uppercase text-[#7cff01]">
                    {user?.initials || 'U'}
                  </div>
                )}
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-[#7cff01] leading-none">Scouta</h1>
                <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                  {user?.name} <span className="opacity-50">|</span> {user?.role === 'scout' ? 'Scout' : user?.role === 'club' ? 'Club' : user?.role}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab('messages')}
              className="relative flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-gray-200 transition text-xs font-black uppercase tracking-widest"
            >
              <MessageSquare size={16} />
              <span className="hidden sm:inline">Messages</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 bg-red-500 text-white text-[10px] font-black rounded-full leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-gray-200 transition text-xs font-black uppercase tracking-widest"
            >
              <Settings2 size={16} />
              <span className="hidden sm:inline">Settings</span>
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-red-900/10 hover:bg-red-900/20 border border-red-900/30 rounded-xl text-red-400 transition text-xs font-black uppercase tracking-widest"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-t border-gray-800 flex gap-1 px-4 py-0 bg-gray-950 overflow-x-auto whitespace-nowrap hide-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => showTab(tab)}
              className={`px-4 py-3 font-medium text-sm transition border-b-2 whitespace-nowrap flex-shrink-0 ${
                activeTab === tab
                  ? 'border-green-500 text-green-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab === 'explore' && 'Explore'}
              {tab === 'log' && 'Log Player'}
              {tab === 'database' && 'Database'}
              {tab === 'recorder' && 'Match Recorder'}
              {tab === 'insights' && 'My Insights'}
              {tab === 'report' && 'Match Reports'}
              {tab === 'global_insights' && 'Global Insights'}
              {tab === 'club' && 'Club Dashboard'}
              {tab === 'import' && 'Bulk Import'}
              {tab === 'accounts' && 'Manage Accounts'}
              {tab === 'clubs' && 'Manage Teams'}
              {tab === 'claims' && 'Profile Claims'}
              {tab === 'announcements' && 'Announcements'}
              {tab === 'notifications' && 'Notifications'}
              {tab === 'blog' && 'Blog Posts'}
              {tab === 'settings' && 'Settings'}
              {tab === 'messages' && (
                <span className="relative">
                  Messages
                  {unreadCount > 0 && (
                    <span className="absolute -top-2 -right-3 min-w-[18px] h-[18px] flex items-center justify-center px-1 bg-red-500 text-white text-[10px] font-black rounded-full leading-none">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </span>
              )}
              {tab === 'saved' && 'Saved'}
              {tab === 'affiliates' && 'Affiliates'}
            </button>
          ))}
        </div>
      </header>

      {/* Content */}
      <main className={`flex-1 ${activeTab === 'messages' ? 'flex flex-col overflow-hidden -mx-4 sm:mx-0' : 'max-w-7xl mx-auto px-4 py-8'}`}>
        {renderTabContent()}
      </main>
    </div>
  )
}

export default Dashboard
